import SelectAOIIcon from '@apps-common/ui-icons/SelectAoi.jsx';
import LayerSwitcher from '@apps-common/ui-maps/LayerSwitcher.jsx';
import MapTool from '@apps-common/ui-maps/MapTool.jsx';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import {ListItem, ListItemText, Switch} from '@mui/material';
import {array, bool, func, object, string} from 'prop-types';
import React, {useCallback, useState} from 'react';
import {connect, useDispatch, useSelector} from 'react-redux';
import {setBaseLayer, setOverlayOn} from '@explorer/actions/map';
import {clearSearch, defineGeometry} from '@explorer/actions/search';
import {trackEvent} from '@explorer/analytics';
import AdditionalZoomTools from '@explorer/components/map/additional-zoom-tools';
import DrawTools from '@explorer/components/map/draw-tools';
import {measureTooltipMap} from '@explorer/components/map/draw-tools/tooltips';
import MapToolsGroup from '@explorer/components/map/map-tools-group';
import {RulerIcon} from '@explorer/components/map/ruler-icon';
import LinkShareTool from '@explorer/components/map/share-tool';
import ZoomControls from '@explorer/components/map/zoom-controls';
import {
  DEFAULT_ZOOM,
  MAP_DRAW_LAYERS,
  MAP_TOOLS,
  MAX_FOLDERS_ZOOM,
  MAX_ZOOM,
  SETTINGS_KEY,
  TILE_MODE,
  TOUR_STAGES,
  UI_MODES,
} from '@explorer/constants';
import {OVERLAYS} from '@explorer/constants/map.js';
import {get as getStored, set as setStored} from '@explorer/util/store.js';

const MENUS = {
  MEASURE: 'measure-menu',
  AOI: 'aoi-menu',
  LAYERS: 'layers-menu',
  ZOOM: 'zoom-menu',
};

const MapControls = ({
  clearSearch,
  defineGeometry,
  isComparing,
  measureFeatures,
  searchFeatures,
  setHoverFeatures,
  setMeasureFeatures,
  setSearchFeatures,
  setTempGeometry,
  tempGeometry,
  tileMode,
}) => {
  const [menu, setMenu] = useState('');
  const [menuTarget, setMenuTarget] = useState(null);

  const dispatch = useDispatch();
  const labelsOn = useSelector(state => state.map.overlays.labels);
  const centerMarkerOn = useSelector(state => state.map.overlays.centerMarker);
  const selectedBackground = useSelector(state => state.map.baseLayer);

  const LAYERS_LABELS = {
    centerMarker: 'Center marker',
  };

  const olMap = {};

  const toggleMenu = useCallback(
    (nextMenu, anchor) => {
      if (nextMenu === menu) {
        setMenu('');
      } else {
        setMenuTarget(anchor);
        setMenu(nextMenu);
      }
    },
    [menu, setMenu, setMenuTarget]
  );

  const deleteMeasureFeature = useCallback(
    feature => {
      trackEvent('Cleared measurement feature');
      setMeasureFeatures(measureFeatures.filter(f => f.id !== feature.id));
    },
    [measureFeatures, setMeasureFeatures]
  );

  const changeMeasureFeatureVisibility = useCallback(
    feature => {
      trackEvent('Toggled feature visibility');
      setMeasureFeatures(
        measureFeatures.map(f => {
          if (f.id === feature.id) {
            const fprops = f.properties || {};

            f.properties = {
              ...fprops,
              visible: !(
                fprops.visible === undefined || fprops.visible === true
              ),
            };
          }
          return f;
        })
      );
    },
    [measureFeatures, setMeasureFeatures]
  );

  const onHoverFeature = useCallback(
    feature => {
      if (feature) {
        setHoverFeatures([feature]);
      } else {
        setHoverFeatures([]);
      }
    },
    [setHoverFeatures]
  );

  const showDrawingTools = !isComparing && tileMode !== TILE_MODE.FOLDERS;

  const toggleLayers = useCallback(
    value => {
      dispatch(setBaseLayer(value));
      setStored(SETTINGS_KEY, {
        ...getStored(SETTINGS_KEY),
        baseLayer: value,
      });
      trackEvent('Toggled base layer', {
        name: value,
      });
    },
    [dispatch]
  );

  return (
    <React.Fragment>
      <MapToolsGroup>
        <LinkShareTool />
        {menu === MENUS.AOI && (
          <DrawTools
            anchorEl={menuTarget}
            open
            features={searchFeatures}
            tempGeometry={tempGeometry}
            setTempGeometry={setTempGeometry}
            placement="top-end"
            drawLayer={MAP_DRAW_LAYERS.GEOMETRY}
            onDelete={() => {
              trackEvent('Cleared search AOI');
              clearSearch();
            }}
            canToggleVisibility={false}
            uploadAvailable
            onSave={() => {
              if (tempGeometry) {
                setSearchFeatures([
                  {type: 'Feature', id: 'tmp-id', geometry: tempGeometry},
                ]);

                defineGeometry({geometry: tempGeometry});
              }
            }}
            onHover={onHoverFeature}
          />
        )}

        {showDrawingTools && (
          <MapTool
            data-tour={TOUR_STAGES.DRAW_TOOLS}
            value="aoi-tool"
            tooltipTitle="Draw or upload an area of interest"
            active={menu === MENUS.AOI}
            icon={<SelectAOIIcon />}
            onClick={evt => {
              toggleMenu(MENUS.AOI, evt.currentTarget);
            }}
          />
        )}

        {menu === MENUS.MEASURE && (
          <DrawTools
            anchorEl={menuTarget}
            open
            tempGeometry={tempGeometry}
            setTempGeometry={setTempGeometry}
            features={measureFeatures}
            setFeatures={setMeasureFeatures}
            placement="top-end"
            drawLayer={MAP_DRAW_LAYERS.MEASURE}
            title="Measure"
            toolsAvailable={[MAP_TOOLS.DRAW_POLYGON, MAP_TOOLS.DRAW_LINE]}
            tooltips={measureTooltipMap}
            onDelete={deleteMeasureFeature}
            onChangeVisibility={changeMeasureFeatureVisibility}
            onHover={onHoverFeature}
            onSave={feature => {
              if (tempGeometry) {
                for (let i = 0, ii = measureFeatures.length; i < ii; i++) {
                  if (measureFeatures[i].id === feature.id) {
                    measureFeatures[i] = {
                      ...feature,
                      geometry: tempGeometry,
                    };
                  }
                }
                setMeasureFeatures(measureFeatures);
              }
            }}
          />
        )}

        <MapTool
          value="measure-tool"
          tooltipTitle="Measure areas and distances"
          tooltipPosition="left-end"
          active={menu === MENUS.MEASURE}
          icon={<RulerIcon />}
          onClick={evt => {
            toggleMenu(MENUS.MEASURE, evt.currentTarget);
          }}
        />
      </MapToolsGroup>

      <MapToolsGroup>
        <LayerSwitcher
          selectedBackground={selectedBackground}
          labelsOn={labelsOn}
          onBackgroundChange={toggleLayers}
          onLabelsChange={() =>
            dispatch(setOverlayOn({name: OVERLAYS.LABELS, on: !labelsOn}))
          }
        >
          <ListItem
            secondaryAction={
              <Switch
                color="primary"
                edge="end"
                onChange={() =>
                  dispatch(
                    setOverlayOn({
                      name: OVERLAYS.CENTER_MARKER,
                      on: !centerMarkerOn,
                    })
                  )
                }
                checked={centerMarkerOn}
              />
            }
            sx={{
              '& .MuiListItemSecondaryAction-root': {
                right: 2,
              },
            }}
          >
            <ListItemText primary={LAYERS_LABELS.centerMarker} />
          </ListItem>
        </LayerSwitcher>
      </MapToolsGroup>

      <MapToolsGroup>
        {menu === MENUS.ZOOM && (
          <AdditionalZoomTools
            open
            anchorEl={menuTarget}
            map={olMap}
            placement="left-end"
            onChangeTool={() => {
              toggleMenu('', null);
            }}
          />
        )}

        <ZoomControls
          maxZoom={tileMode === TILE_MODE.FOLDERS ? MAX_FOLDERS_ZOOM : MAX_ZOOM}
          minZoom={DEFAULT_ZOOM}
          additionalControls={
            <MapTool
              value="zoom-tools"
              tooltipTitle="Zoom tools"
              tooltipPosition="left-end"
              active={menu === MENUS.ZOOM}
              icon={<ZoomInIcon />}
              onClick={evt => {
                toggleMenu(MENUS.ZOOM, evt.currentTarget);
              }}
              sx={{
                borderRadius: '0 0 4px 4px',
                boxShadow: 'none',
              }}
            />
          }
        />
      </MapToolsGroup>
    </React.Fragment>
  );
};

MapControls.propTypes = {
  clearSearch: func,
  defineGeometry: func,
  isComparing: bool,
  measureFeatures: array,
  searchFeatures: array,
  setHoverFeatures: func,
  setMeasureFeatures: func,
  setSearchFeatures: func,
  setTempGeometry: func,
  tempGeometry: object,
  tileMode: string,
};

const mapStateToProps = state => ({
  isComparing: state.ui.mode === UI_MODES.COMPARE,
  tileMode: state.tiles.mode,
});

const mapDispatchToProps = {
  clearSearch,
  defineGeometry,
};

export default connect(mapStateToProps, mapDispatchToProps)(MapControls);
